import { useContext, createContext } from 'react';
import { ChatMessage } from '@/types/chatContext';
import type { PendingAction } from '@/types/chat';

export interface OpenChatOptions {
  // Scope keys use presence semantics: pass the key (even null) to set scope;
  // omit it to leave the current scope untouched.
  recipeId?: string | null;
  collectionId?: string | null;
  helpMode?: boolean;
  /** Action to queue as the chat opens; the surface handles it (substitution auto-sends). */
  pendingAction?: PendingAction;
}

interface ChatContextType {
  conversationId: string | null;
  messages: ChatMessage[];
  currentRecipeId: string | null;
  currentCollectionId: string | null;
  pendingAction: PendingAction | null;
  desktopChatOpen: boolean;
  helpMode: boolean;
  setConversationId: (id: string | null) => void;
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  setCurrentRecipeId: (id: string | null) => void;
  setCurrentCollectionId: (id: string | null) => void;
  setPendingAction: (action: PendingAction | null) => void;
  setDesktopChatOpen: (open: boolean) => void;
  setHelpMode: (open: boolean) => void;
  /** Single entry point to open the chat. */
  openChat: (opts?: OpenChatOptions) => void;
  closeChat: () => void;
  resetChat: () => void;
}

export const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function useChatContext() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
}
